import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import { Add } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import uuid from 'uuid';
import useRouter from '../../../services/useRouter';
import Layout from '../../../components/Layout';
import Container from '../../../components/Base/Container';
import TextInput from '../../../components/Base/Input/TextInput';
import Loader from '../../../components/Base/Loader';
import {
  setsDestroyRequest,
  setsShowRequest, setsShowReset, setsStoreRequest, setsUpdateRequest,
} from '../../../providers/redux/actions/Sets';
import Prices from '../../../components/Prices';
import Option from '../../../components/Option';
import IconButton from '../../../components/Base/Button/IconButton/IconButton';
import Constants from '../../../constants';
import Toolbar
  from '../../../components/Base/ModelInteractor/Components/Toolbar';
import usePrevious from '../../../providers/hooks/usePrevious';
import ButtonBack from '../../../components/Base/Button/ButtonBack';
import PageHeader from '../../../components/PageHeader';
import ButtonGlow from '../../../components/Buttons/ButtonGlow';
import useNavigate from '../../../services/useNavigate';
import fastCompare from '../../../providers/fastCompare';
import ImagePicker from '../../../components/Base/Image/ImagePicker';

const useStyles = makeStyles(
  theme => ({
    container: {
      flexDirection: 'row',
    },
    backContainer: {
      padding: theme.spacing(6),
    },
    prices: {
      marginTop: theme.spacing(4),
      marginLeft: theme.spacing(12.5),
    },
    segment: {
      marginLeft: theme.spacing(12.5),
      padding: theme.spacing(),
    },
    options: {
      marginTop: theme.spacing(4),
      display: 'flex',
      flexDirection: 'row',
    },
    optionTitle: {
      flex: 1,
    },
    button: {
      display: 'block',
      width: '100%',
      marginTop: theme.spacing(7),
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
    },
    inputs: {
      flex: 1,
      marginLeft: theme.spacing(4),
    },
  }),
);

const ScreenSet = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const original = useRef();
  const { match } = useRouter();
  const { id } = match.params;

  const {
    destroyLoading,
    destroySuccess,
    storeSuccess,
    storeLoading,
    updateLoading,
    updateSuccess,
    loading,
    data,
    errors,
  } = useSelector(
    state => ({
      destroyLoading: state.setsReducer.destroy.loading,
      destroySuccess: state.setsReducer.destroy.success,
      storeLoading: state.setsReducer.store.loading,
      storeSuccess: state.setsReducer.store.success,
      updateLoading: state.setsReducer.update.loading,
      updateSuccess: state.setsReducer.update.success,
      loading: state.setsReducer.show.loading,
      data: state.setsReducer.show.data,
      errors: state.setsReducer.show.errors,
    }),
  );

  const { name: nameErrors } = errors;

  const [name, setName] = useState('');
  const [image, setImage] = useState(null);
  const [price, setPrice] = useState('');
  const [prices, setPrices] = useState([]);
  const [options, setOptions] = useState([
    {
      uuid: uuid(),
      name: '',
      choices: [
        {
          uuid: uuid(),
          ...Constants.value.choice,
        },
      ],
    },
  ]);

  const previousPrice = usePrevious(price);

  const resetSubmission = () => {
    setName('');
    setImage(null);
    setPrice('');
    setPrices([]);
    setOptions([
      {
        uuid: uuid(),
        name: '',
        choices: [
          {
            uuid: uuid(),
            ...Constants.value.choice,
          },
        ],
      },
    ]);
  };

  const onBack = () => navigate(Constants.routes.Menu);

  const onDestroy = () => dispatch(
    setsDestroyRequest(id),
  );

  const onEdit = () => dispatch(
    setsUpdateRequest(id, {
      name,
      image,
      price,
      prices: prices.map(
        ({ id: storeId, price: storePrice }) => ({
          storeId,
          price: storePrice,
        }),
      ),
      options,
    }),
  );

  const onSubmit = () => dispatch(
    setsStoreRequest({
      name,
      price,
      prices: prices.map(
        ({ id: storeId, price: storePrice }) => ({
          storeId,
          price: storePrice,
        }),
      ),
      options,
    }),
  );

  const setStorePrice = (storeId, storePrice) => {
    const newPrices = [
      ...prices.filter(
        ({ id: priceStoreId }) => priceStoreId !== storeId,
      ),
      {
        ...prices.find(
          ({ id: priceStoreId }) => priceStoreId === storeId,
        ),
        price: storePrice,
      },
    ];

    setPrices(newPrices);
  };

  const onChoiceNameChange = (index, newName) => {
    const newOptions = [...options];
    const option = options[index];
    option.name = newName;
    options[index] = option;
    setOptions(newOptions);
  };

  const onChoicesChange = (index, newChoices) => {
    const newOptions = [...options];
    const option = options[index];
    option.choices = newChoices;
    options[index] = option;
    setOptions(newOptions);
  };

  const onAddOption = () => {
    const newOptions = [...options];
    newOptions.push({
      uuid: uuid(),
      name: '',
      choices: [
        { ...Constants.value.choice },
      ],
    });

    setOptions(newOptions);
  };

  const onOptionDestroy = (key) => {
    const newOptions = [...options];
    newOptions.splice(key, 1);
    setOptions(newOptions);
  };

  const postLoad = () => {
    const {
      name: originalSetName,
      image: originalImage,
      price: originalSetPrice,
      stores: originalSetPrices,
      options: originalSetOptions,
    } = data;

    const mappedPrices = originalSetPrices.map(
      store => ({
        ...store,
        price: store.price.toFixed(2),
      }),
    );

    const mappedOptions = originalSetOptions.map(
      option => ({
        ...option,
        uuid: uuid(),
        price: option.price.toFixed(2),
        choices: option.choices.map(
          choice => ({
            ...choice,
            uuid: uuid(),
            price: choice.price.toFixed(2),
          }),
        ),
      }),
    );

    setName(originalSetName);
    setImage(originalImage);
    setPrice(originalSetPrice.toFixed(2));
    setPrices(mappedPrices);
    setOptions([..._.cloneDeep(mappedOptions)]);

    original.current = {
      name: originalSetName,
      image: originalImage,
      price: originalSetPrice.toFixed(2),
      prices: mappedPrices,
      options: [..._.cloneDeep(mappedOptions)],
    };
  };

  const onEnter = id ? onEdit : onSubmit;
  const isSame = original.current ? fastCompare(original.current, {
    name,
    image,
    price,
    prices,
    options,
  }) : false;

  useEffect(() => {
    if (!destroyLoading && destroySuccess) {
      onBack();
    }
  }, [destroyLoading, destroySuccess]);

  useEffect(() => {
    if (id) {
      dispatch(
        setsShowRequest(id),
      );
      return;
    }

    dispatch(
      setsShowReset(),
    );
  }, [id]);

  useEffect(() => {
    if (!storeLoading && storeSuccess) {
      resetSubmission();
      onBack();
    }
  }, [storeLoading, storeSuccess]);

  useEffect(() => {
    const storePrices = [...prices];
    const formatPreviousPrice = parseFloat(previousPrice) || 0;
    setPrices(
      storePrices.map(
        (originalPrices) => {
          const formatStorePrice = parseFloat(originalPrices.price) || 0;
          if (formatPreviousPrice === formatStorePrice) {
            return {
              ...originalPrices,
              price,
            };
          }

          return originalPrices;
        },
      ),
    );
  }, [price]);

  useEffect(() => {
    if (data) {
      postLoad();
      return;
    }

    resetSubmission();
  }, [data]);

  useEffect(() => {
    if (!updateLoading && updateSuccess) {
      postLoad();
    }
  }, [updateLoading, updateSuccess]);

  if (loading && !data) {
    return (
      <Layout flex center>
        <Container flex center alignCenter>
          <Loader />
        </Container>
      </Layout>
    );
  }

  if (id && !data) {
    return (
      <Layout>
        <Typography variant="body1">Could not find item.</Typography>
      </Layout>
    );
  }

  return (
    <Layout flex>
      <Container className={classes.container} flex scroll>
        <Container className={classes.container} flex>
          <Container className={classes.backContainer}>
            <ButtonBack onPress={onBack} />
          </Container>
          <Container>
            <PageHeader title={id ? `Viewing ${name}` : 'New set menu'} />
            <Container className={classNames(classes.segment, classes.row)}>
              <ImagePicker
                key={image}
                onChange={setImage}
                image={image}
              />
              <Container className={classes.inputs}>
                <TextInput
                  label="Name"
                  onChange={setName}
                  value={name}
                  errors={nameErrors}
                  placeholder="Waffle"
                  onEnter={onEnter}
                  shrink
                />
              </Container>
            </Container>
            <Prices
              className={classes.prices}
              standardPrice={price}
              onStandardPriceChange={setPrice}
              standardPriceErrors={errors.price}
              prices={prices}
              onEnter={onEnter}
              onChange={setStorePrice}
            />
            <Container className={classes.segment}>
              <Container className={classes.options}>
                <Typography className={classes.optionTitle} variant="h5">
                  Options
                </Typography>
                <Container>
                  <IconButton onPress={onAddOption}>
                    <Add />
                  </IconButton>
                </Container>
              </Container>
              {options.map(
                ({
                  uuid: optionUuid,
                  name: choiceName,
                  choices,
                }, key) => (
                  <Option
                    key={optionUuid}
                    uuid={optionUuid}
                    choices={choices}
                    onChangeName={newName => onChoiceNameChange(key, newName)}
                    onChangeChoices={newChoices => onChoicesChange(
                      key,
                      newChoices,
                    )}
                    name={choiceName}
                    onDestroy={() => onOptionDestroy(key)}
                    onEnter={onEnter}
                    isSetMenu
                  />
                ),
              )}
            </Container>
            {!id && (
              <Container className={classes.segment}>
                <ButtonGlow className={classes.button} onPress={onSubmit}>
                  Add set
                </ButtonGlow>
              </Container>
            )}
          </Container>
        </Container>
      </Container>
      {id && (
        <Toolbar
          fireDelete={onDestroy}
          fireEdit={onEdit}
          processing={loading}
          edit={!isSame}
        />
      )}
    </Layout>
  );
};

export default ScreenSet;
